import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useRef } from 'react'
import { cn } from '@/lib/utils'
import {
    gsap,
    isDesktopViewport,
    MOTION_QUERIES,
    prefersReducedMotion,
    useGSAP
} from '@/lib/motion'
import SplitChars from '@/components/landing/split-chars'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'
import GeometricBackdrop from '@/components/landing/decor/geometric-backdrop'
import { HERO_COPY, OPENING_COPY } from '@/constants/content'

const HERO_STARS = [
    { x: 13, y: 14, size: 18, gold: true, delay: 0.2 },
    { x: 24, y: 31, size: 10, gold: false, delay: 1.4 },
    { x: 33, y: 9, size: 13, gold: true, delay: 2.1 },
    { x: 42, y: 23, size: 8, gold: false, delay: 0.8 },
    { x: 58, y: 12, size: 22, gold: true, delay: 1.1 },
    { x: 65, y: 29, size: 9, gold: false, delay: 2.6 },
    { x: 72, y: 7, size: 12, gold: true, delay: 0.4 },
    { x: 79, y: 21, size: 16, gold: false, delay: 1.8 },
    { x: 88, y: 12, size: 10, gold: true, delay: 3 },
    { x: 7, y: 39, size: 9, gold: true, delay: 2.4 },
    { x: 93, y: 37, size: 12, gold: true, delay: 0.6 },
    { x: 49, y: 6, size: 9, gold: false, delay: 3.4 },
    { x: 18, y: 52, size: 7, gold: false, delay: 1 },
    { x: 85, y: 51, size: 8, gold: false, delay: 2 }
]

const waitForImage = (image: HTMLImageElement | null): Promise<void> => {
    if (!image) return Promise.resolve()
    if (image.complete && image.naturalWidth > 0) return Promise.resolve()

    return image.decode().catch(() => undefined)
}

const wait = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms))

// TOP: a white opening light, then the key visual assembles itself in order (background, stars,
// Mahonia confusa, the character, AMAMA NIA, the catchcopy). On scroll every layer drifts at its own
// speed for depth, and the scene dissolves into white light before PROFILE.
const HeroSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const curtainRef = useRef<HTMLDivElement>(null)
    const characterRef = useRef<HTMLImageElement>(null)

    useGSAP(() => {
        const section = sectionRef.current
        const curtain = curtainRef.current
        const character = characterRef.current

        if (!section || !curtain || !character) return

        curtain.setAttribute('data-ready', '')

        const q = gsap.utils.selector(section)
        const header = document.querySelector<HTMLElement>('[data-site-header]')

        if (prefersReducedMotion()) {
            gsap.to(curtain, { autoAlpha: 0, duration: 0.4, delay: 0.2 })

            return
        }

        const desktop = isDesktopViewport()
        const blur = (amount: number): string => (desktop ? `blur(${amount}px)` : 'blur(0px)')

        const background = q('[data-hero-intro="bg"]')
        const stars = q('[data-hero-star]')
        const plantLeft = q('[data-hero-intro="plant-left"]')
        const plantRight = q('[data-hero-intro="plant-right"]')
        const plantSmall = q('[data-hero-intro="plant-small"]')
        const titleChars = q('[data-hero-title] [data-char]')
        const catchChars = q('[data-hero-catch] [data-char]')
        const extras = q('[data-hero-fade]')
        const bar = q('[data-curtain-bar]')

        gsap.set(character, { transformOrigin: '50% 100%' })
        gsap.set(background, { autoAlpha: 0, scale: 1.14 })
        gsap.set(stars, { autoAlpha: 0, scale: 0.3 })
        gsap.set(plantLeft, { autoAlpha: 0, xPercent: -32, yPercent: 16, rotate: -9 })
        gsap.set(plantRight, { autoAlpha: 0, xPercent: 32, yPercent: 16, rotate: 9 })
        gsap.set(plantSmall, { autoAlpha: 0, yPercent: 30 })
        gsap.set(character, { autoAlpha: 0, yPercent: 10, filter: blur(16) })
        gsap.set(titleChars, { autoAlpha: 0, y: 46, filter: blur(12) })
        gsap.set(catchChars, { autoAlpha: 0, y: 14 })
        gsap.set(extras, { autoAlpha: 0 })

        if (header) gsap.set(header, { autoAlpha: 0 })

        const loading = gsap.to(bar, { scaleX: 0.82, duration: 2.6, ease: 'power2.out' })

        const intro = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } })

        intro
            .to(bar, { scaleX: 1, duration: 0.35, ease: 'power1.out' })
            .to(q('[data-curtain-star]'), { scale: 2.6, autoAlpha: 0, duration: 1, ease: 'power2.in' }, '<0.1')
            .to(q('[data-curtain-text]'), { autoAlpha: 0, y: -6, duration: 0.6, stagger: 0.05 }, '<')
            .to(curtain, { autoAlpha: 0, duration: 1.5, ease: 'sine.inOut' }, '-=0.35')
            // 1. background
            .to(background, { autoAlpha: 1, scale: 1, duration: 3.4, ease: 'power2.out' }, '<')
            // 2. ✦ stars
            .to(stars, { autoAlpha: 1, scale: 1, duration: 1.6, ease: 'power2.out', stagger: { each: 0.09, from: 'random' } }, '-=2.6')
            // 3. Mahonia confusa
            .to(plantLeft, { autoAlpha: 1, xPercent: 0, yPercent: 0, rotate: 0, duration: 2.6 }, '-=1.3')
            .to(plantRight, { autoAlpha: 1, xPercent: 0, yPercent: 0, rotate: 0, duration: 2.6 }, '<0.18')
            .to(plantSmall, { autoAlpha: 1, yPercent: 0, duration: 2.2 }, '<0.3')
            // 4. character
            .to(character, { autoAlpha: 1, yPercent: 0, filter: 'blur(0px)', duration: 2.8 }, '-=1.9')
            // 5. AMAMA NIA
            .to(titleChars, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 1.8, stagger: 0.1 }, '-=1.4')
            // 6. catchcopy
            .to(catchChars, { autoAlpha: 1, y: 0, duration: 1.3, stagger: 0.07, ease: 'power2.out' }, '-=0.9')
            .to(extras, { autoAlpha: 1, duration: 1.4, stagger: 0.08, ease: 'sine.out' }, '-=0.8')
            .set([character, ...titleChars], { clearProps: 'filter' })

        if (header) intro.to(header, { autoAlpha: 1, duration: 1.4, ease: 'sine.out' }, '-=1.6')

        let cancelled = false
        const ready = Promise.all([waitForImage(character), wait(900)])

        Promise.race([ready, wait(4000)]).then(() => {
            if (cancelled) return

            loading.kill()
            intro.play()
        })

        const mm = gsap.matchMedia()

        mm.add({ desktop: MOTION_QUERIES.desktop, mobile: MOTION_QUERIES.mobile }, context => {
            const isDesktop = Boolean(context.conditions?.desktop)

            gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom bottom',
                    // No invalidateOnRefresh here: a refresh would revert the character's transform,
                    // which the opening sequence animates at the same time.
                    scrub: 1.2
                }
            })
                .to(q('[data-hero-layer="bg"]'), { yPercent: -5, scale: 1.08, duration: 1 }, 0)
                .to(q('[data-hero-layer="stars"]'), { yPercent: -14, duration: 1 }, 0)
                .to(q('[data-hero-layer="glow"]'), { yPercent: -8, scale: 1.08, duration: 1 }, 0)
                .to(character, { y: () => -window.innerHeight * (isDesktop ? 0.16 : 0.1), scale: isDesktop ? 1.06 : 1.03, duration: 1 }, 0)
                .to(q('[data-hero-layer="plant-left"]'), { y: () => -window.innerHeight * 0.34, x: () => -window.innerWidth * 0.06, rotate: -5, duration: 1 }, 0)
                .to(q('[data-hero-layer="plant-right"]'), { y: () => -window.innerHeight * 0.3, x: () => window.innerWidth * 0.06, rotate: 5, duration: 1 }, 0)
                .to(q('[data-hero-layer="plant-small"]'), { y: () => -window.innerHeight * 0.22, duration: 1 }, 0)
                .to(q('[data-hero-layer="title"]'), { y: () => -window.innerHeight * 0.12, autoAlpha: 0, duration: 0.55 }, 0.08)
                .to(q('[data-hero-layer="catch"]'), { y: () => -window.innerHeight * 0.2, autoAlpha: 0, duration: 0.5 }, 0)
                .to(q('[data-hero-layer="hud"]'), { autoAlpha: 0, duration: 0.3 }, 0)
                .to(q('[data-hero-flash]'), { autoAlpha: 1, duration: 0.32, ease: 'power1.in' }, 0.68)
        })

        return () => {
            cancelled = true
        }
    }, { scope: sectionRef })

    return (
        <section
            id='top'
            ref={sectionRef}
            aria-label='トップ'
            className='relative h-[165svh] lg:h-[230vh]'
        >
            <div className='sticky top-0 h-svh overflow-hidden bg-[#dcebf5]'>
                <div data-hero-layer='bg' className='absolute inset-0 will-change-transform'>
                    <div data-hero-intro='bg' className='absolute inset-[-4%]'>
                        <GeometricBackdrop className='absolute inset-0 h-full w-full' />
                    </div>
                    <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.06)_45%,rgba(251,250,246,0.72)_84%,#fbfaf6_100%)]' />
                </div>

                <div className='pointer-events-none absolute inset-x-0 bottom-[-24svh] flex justify-center'>
                    <div
                        data-hero-layer='glow'
                        className='aspect-square w-[min(125vh,150vw)] shrink-0 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.98),rgba(255,255,255,0.86)_40%,rgba(255,255,255,0.35)_63%,rgba(255,255,255,0)_80%)]'
                    />
                </div>

                <div data-hero-layer='stars' className='pointer-events-none absolute inset-0'>
                    {HERO_STARS.map((star, index) => (
                        <span
                            key={index}
                            data-hero-star
                            className={cn('absolute block', index > 9 && 'hidden sm:block')}
                            style={{ left: `${star.x}%`, top: `${star.y}%` }}
                        >
                            <Sparkle
                                className={cn(
                                    'block motion-safe:animate-twinkle',
                                    star.gold
                                        ? 'text-gold drop-shadow-[0_0_6px_rgba(255,226,150,0.9)]'
                                        : 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.9)]'
                                )}
                                style={{ width: star.size, height: star.size, animationDelay: `${star.delay}s` }}
                            />
                        </span>
                    ))}
                </div>

                <div className='pointer-events-none absolute inset-x-0 bottom-[-3svh] flex justify-center'>
                    <Image
                        ref={characterRef}
                        src='/images/kv-character.png'
                        alt='カメラを手に、夏の光の中に立つ AMAMA NIA のキービジュアル'
                        width={1696}
                        height={2528}
                        preload
                        sizes='(min-width: 1024px) 45vw, 90vw'
                        className='h-[84svh] w-auto max-w-none shrink-0 select-none mix-blend-multiply will-change-transform lg:h-[100svh]'
                    />
                </div>

                <div data-hero-layer='plant-small' className='pointer-events-none absolute bottom-[4svh] left-[24vw] hidden w-[11vw] max-w-[180px] lg:block'>
                    <div data-hero-intro='plant-small'>
                        <div className='origin-bottom motion-safe:animate-sway' style={{ animationDuration: '6.4s' }}>
                            <MahoniaSprig variant='flower' seed={11} />
                        </div>
                    </div>
                </div>

                <div data-hero-layer='plant-left' className='pointer-events-none absolute bottom-[-8svh] left-[-12vw] w-[48vw] max-w-[480px] lg:left-[-3vw] lg:w-[25vw]'>
                    <div data-hero-intro='plant-left'>
                        <div className='origin-bottom motion-safe:animate-sway'>
                            <MahoniaSprig seed={3} />
                        </div>
                    </div>
                </div>

                <div data-hero-layer='plant-right' className='pointer-events-none absolute bottom-[-10svh] right-[-14vw] w-[46vw] max-w-[460px] lg:right-[-4vw] lg:w-[23vw]'>
                    <div data-hero-intro='plant-right'>
                        <div className='origin-bottom motion-safe:animate-sway' style={{ animationDuration: '8.6s', animationDelay: '-2s' }}>
                            <MahoniaSprig seed={7} className='-scale-x-100' />
                        </div>
                    </div>
                </div>

                <div data-hero-layer='title' className='absolute inset-x-0 bottom-[11svh] flex flex-col items-center px-4 lg:bottom-[6vh]'>
                    <h1
                        data-hero-title
                        className='flex flex-col items-center text-center font-heading text-[clamp(3.2rem,16vw,7rem)] font-light leading-[0.86] tracking-[0.14em] text-ink [text-shadow:0_2px_40px_rgba(255,255,255,0.85)] lg:text-[clamp(5rem,9.2vw,10rem)]'
                    >
                        <span className='sr-only'>{HERO_COPY.title}</span>
                        {HERO_COPY.titleLines.map(line => (
                            <span key={line} aria-hidden='true' className='block pl-[0.14em]'>
                                {Array.from(line).map((char, index) => (
                                    <span key={`${line}-${index}`} data-char className='inline-block'>
                                        {char}
                                    </span>
                                ))}
                            </span>
                        ))}
                    </h1>
                    <p data-hero-fade className='mt-3 font-mono text-[10px] tracking-[0.4em] text-ink/75 lg:text-xs'>
                        {HERO_COPY.catchEn}
                    </p>
                </div>

                <div data-hero-layer='catch' className='absolute right-[5vw] top-[max(19svh,8.5rem)] lg:right-[7vw] lg:top-[max(24vh,12rem)]'>
                    {/* In vertical writing a column flexbox runs right to left, so each line is a column. */}
                    <div className='writing-vertical flex flex-col gap-4 text-ink lg:gap-6'>
                        <p data-hero-catch className='flex flex-col gap-[0.55em] text-[clamp(1.05rem,2.1vw,1.85rem)] font-medium leading-none tracking-[0.32em] [text-shadow:0_1px_18px_rgba(255,255,255,0.9)]'>
                            <span className='sr-only'>{HERO_COPY.catch}</span>
                            {HERO_COPY.catchLines.map((line, index) => (
                                <span key={line} aria-hidden='true' className={cn('block', index > 0 && 'pt-[2.6em]')}>
                                    <SplitChars text={line} />
                                </span>
                            ))}
                        </p>
                        <p data-hero-fade className='hidden text-xs tracking-[0.3em] text-ink/75 [text-shadow:0_1px_14px_rgba(255,255,255,0.9)] sm:block'>
                            {HERO_COPY.sub}
                        </p>
                    </div>
                </div>

                <div data-hero-layer='hud' className='pointer-events-none absolute inset-0 font-mono text-[10px] tracking-[0.3em] text-ink/75'>
                    <div data-hero-fade className='absolute left-5 top-20 flex flex-col gap-1.5 lg:left-10 lg:top-28'>
                        <span className='flex items-center gap-2'>
                            <span className='size-1.5 rounded-full bg-coral motion-safe:animate-onair' />
                            {HERO_COPY.log}
                        </span>
                        <span>{HERO_COPY.coords}</span>
                        <span className='hidden lg:block'>{HERO_COPY.day}</span>
                    </div>
                    <div data-hero-fade className='absolute bottom-6 left-5 flex flex-col items-center gap-3 lg:left-10'>
                        <span className='writing-vertical text-[10px] tracking-[0.42em]'>SCROLL</span>
                        <span className='relative block h-14 w-px overflow-hidden bg-ink/15'>
                            <span className='absolute inset-0 bg-ink/70 motion-safe:animate-scroll-line' />
                        </span>
                        <span className='sr-only'>{HERO_COPY.scroll}</span>
                    </div>
                </div>

                <div data-hero-flash aria-hidden='true' className='pointer-events-none absolute inset-0 bg-mist opacity-0' />
            </div>

            <div
                ref={curtainRef}
                aria-hidden='true'
                className='opening-curtain fixed inset-0 z-[100] flex flex-col items-center justify-center bg-mist'
            >
                <div data-curtain-star className='relative'>
                    <span className='absolute inset-[-140%] rounded-full bg-[radial-gradient(closest-side,rgba(226,179,60,0.32),rgba(226,179,60,0))]' />
                    <Sparkle className='relative size-10 text-gold motion-safe:animate-twinkle' />
                </div>
                <p data-curtain-text className='mt-9 flex items-center font-mono text-sm tracking-[0.24em] text-ink/80'>
                    <span aria-hidden='true' className='mr-2 text-gold'>&gt;</span>
                    {OPENING_COPY.greeting}
                    <span aria-hidden='true' className='ml-1 inline-block h-[1.1em] w-[0.5em] bg-ink/60 motion-safe:animate-caret' />
                </p>
                <p data-curtain-text className='mt-3 text-[11px] tracking-[0.24em] text-ink/60'>
                    {OPENING_COPY.status}
                </p>
                <span className='mt-6 block h-px w-32 overflow-hidden bg-ink/10'>
                    <span data-curtain-bar className='block h-full w-full origin-left bg-gold' style={{ transform: 'scaleX(0)' }} />
                </span>
            </div>
        </section>
    )
}

export default HeroSection
